import {createElement, type SVGProps} from 'react';
import type {SvgIconProps} from '@mui/material/SvgIcon';
import type {Components, Theme} from '@mui/material/styles';
import {
    DATA_GRID_GROUP_CHILD_ROW_CLASS,
    DATA_GRID_GROUP_ROW_EXPANDED_CLASS,
    DATA_GRID_PAGINATION_HEIGHT,
    DATA_GRID_ROW_MIN_HEIGHT,
    DATA_GRID_TOOLBAR_HEIGHT,
} from '~/components/data-grid/data-grid.config';
import FilterListIcon from '@mui/icons-material/FilterList';
import ChevronIcon from '~/icons/svg/ChevronIcon.svg?react';
import {COLORS} from '~/style/colors-ts';

/** Collapsible-row toggle: ChevronIcon points right when closed, rotated down when expanded. */
const ROW_TOGGLE_ICON_SIZE = 20;

const RowExpandIcon = (props: SVGProps<SVGSVGElement>) =>
    createElement(ChevronIcon, {width: ROW_TOGGLE_ICON_SIZE, height: ROW_TOGGLE_ICON_SIZE, ...props});

const RowCollapseIcon = (props: SVGProps<SVGSVGElement>) =>
    createElement(ChevronIcon, {
        width: ROW_TOGGLE_ICON_SIZE,
        height: ROW_TOGGLE_ICON_SIZE,
        ...props,
        style: {...props.style, transform: 'rotate(90deg)'},
    });

/**
 * Grid icon slots take a plain component. `FilterListIcon` is overridable, so it cannot be passed directly.
 */
const OpenFilterButtonIcon = (props: SVGProps<SVGSVGElement>) =>
    createElement(FilterListIcon, props as SvgIconProps);

/** Header filter text field height (fits in the 52px filter row). */
const HEADER_FILTER_INPUT_HEIGHT = DATA_GRID_ROW_MIN_HEIGHT.small;

const minRowHeightStyles = (minHeight: number) => ({
    [`&.data-grid-min-row-${minHeight} .MuiDataGrid-row`]: {
        minHeight: minHeight,
    },
    // getRowHeight={() => 'auto'} sets inline minHeight: 'auto' — override so rows never shrink below min.
    [`&.data-grid-min-row-${minHeight} .MuiDataGrid-row--dynamicHeight`]: {
        minHeight: `${minHeight}px !important`,
    },
    [`&.data-grid-min-row-${minHeight}.auto-row-height-centered .MuiDataGrid-row--dynamicHeight .MuiDataGrid-cell`]: {
        minHeight: '100%',
        alignSelf: 'stretch',
        display: 'flex',
        alignItems: 'center',
    },
});

/** Checkbox stays 36px tall regardless of row min height (6px padding + 24px icon). */
const SELECTION_CHECKBOX_PADDING = Math.max(0, (DATA_GRID_ROW_MIN_HEIGHT.small - 24) / 2);

const selectionCheckboxStyles = {
    [`& .MuiDataGrid-cellCheckbox .MuiCheckbox-root`]: {
        padding: SELECTION_CHECKBOX_PADDING,
    },
    [`& .MuiDataGrid-columnHeaderCheckbox .MuiCheckbox-root`]: {
        padding: SELECTION_CHECKBOX_PADDING,
    },
};

const pinnedCellBackground = (backgroundColor: string) => ({
    [`& .MuiDataGrid-cell--pinnedLeft, & .MuiDataGrid-cell--pinnedRight`]: {
        backgroundColor,
    },
});

const groupingRowBackground = (backgroundColor: string) => ({
    backgroundColor,
    [`& .MuiDataGrid-cell`]: {
        backgroundColor,
    },
    ...pinnedCellBackground(backgroundColor),
});

/** Matches small row height; MUI TablePagination toolbar defaults to 52px. */
const paginationStyles = {
    [`& .MuiDataGrid-footerContainer`]: {
        minHeight: DATA_GRID_PAGINATION_HEIGHT + 2,
        height: DATA_GRID_PAGINATION_HEIGHT + 2,
    },
    [`& .MuiTablePagination-root`]: {
        minHeight: DATA_GRID_PAGINATION_HEIGHT + 2,
        height: DATA_GRID_PAGINATION_HEIGHT + 2,
    },
    [`& .MuiTablePagination-toolbar`]: {
        minHeight: DATA_GRID_PAGINATION_HEIGHT,
        height: DATA_GRID_PAGINATION_HEIGHT,
    },
};

const groupingRowStyles = {
    [`& .MuiDataGrid-row.${DATA_GRID_GROUP_ROW_EXPANDED_CLASS}`]: {
        ...groupingRowBackground(COLORS.mithraBlue[100]),
        '&:hover': groupingRowBackground(COLORS.mithraBlue[100]),
    },
    [`& .MuiDataGrid-row.${DATA_GRID_GROUP_CHILD_ROW_CLASS}`]: {
        ...groupingRowBackground(COLORS.grey[50]),
        '&:hover': groupingRowBackground(COLORS.grey[150]),
    },
};

export const dataGridOverrides: Components<Theme>['MuiDataGrid'] = {
    defaultProps: {
        slots: {
            // Every collapsible row (detail panel, tree data, row grouping) toggles with the same arrow.
            detailPanelExpandIcon: RowExpandIcon,
            detailPanelCollapseIcon: RowCollapseIcon,
            treeDataExpandIcon: RowExpandIcon,
            treeDataCollapseIcon: RowCollapseIcon,
            groupingCriteriaExpandIcon: RowExpandIcon,
            groupingCriteriaCollapseIcon: RowCollapseIcon,
            openFilterButtonIcon: OpenFilterButtonIcon,
            // Header-filter row already shows the active filter; don't also badge the title.
            columnHeaderFilterIconButton: () => null,
        },
    },
    styleOverrides: {
        root: ({theme}) => ({
            '--DataGrid-t-color-border-base': theme.palette.divider,
            '--DataGrid-rowBorderColor': theme.palette.divider,
            borderColor: theme.palette.divider,
            ...minRowHeightStyles(DATA_GRID_ROW_MIN_HEIGHT.small),
            ...minRowHeightStyles(DATA_GRID_ROW_MIN_HEIGHT.medium),
            ...selectionCheckboxStyles,
            ...paginationStyles,
            ...groupingRowStyles,
            [`& .MuiDataGrid-columnHeader--filter`]: {
                paddingTop: 4,
                paddingBottom: 4,
            },
            [`& .MuiDataGrid-row.hidden-row, & .MuiDataGrid-row[data-id^="auto-generated-row"]`]: {
                display: 'none',
            },
        }),
        cell: {
            display: 'flex',
            alignItems: 'center',
            '&.hidden-cell': {
                display: 'none',
            },
            '& .hidden-cell': {
                display: 'none',
            },
        },
        columnSeparator: ({theme}) => ({
            color: theme.palette.divider,
        }),
        toolbar: ({theme}) => ({
            minHeight: DATA_GRID_TOOLBAR_HEIGHT,
            height: DATA_GRID_TOOLBAR_HEIGHT,
            paddingTop: 0,
            paddingBottom: 0,
            borderBottom: `1px solid ${theme.palette.divider}`,
        }),
        // Keep header-filter inputs at the compact size for every density.
        columnHeaderFilterInput: {
            marginBottom: 0,
            // Text filters get this class on the TextField wrapper, but a singleSelect filter gets
            // it on the `.MuiInputBase-root` itself — so match the element as well as a descendant.
            [`& .MuiInputBase-root, &.MuiInputBase-root`]: {
                height: HEADER_FILTER_INPUT_HEIGHT,
                minHeight: HEADER_FILTER_INPUT_HEIGHT,
            },
            [`& .MuiInputBase-input, .MuiDataGrid-root--densityCompact & .MuiInputBase-input`]: {
                height: HEADER_FILTER_INPUT_HEIGHT,
                minHeight: HEADER_FILTER_INPUT_HEIGHT,
                boxSizing: 'border-box',
                paddingTop: 0,
                paddingBottom: 0,
            },
            // Doubled class to outrank MUI's own `height: auto` on the select.
            [`& .MuiSelect-select.MuiSelect-select, .MuiDataGrid-root--densityCompact & .MuiSelect-select`]: {
                height: HEADER_FILTER_INPUT_HEIGHT,
                minHeight: HEADER_FILTER_INPUT_HEIGHT,
                boxSizing: 'border-box',
                paddingTop: 0,
                paddingBottom: 0,
                display: 'flex',
                alignItems: 'center',
            },
        },
    },
};
